<template>
  <div
    class="addOnFeaturesEdit"
    :data-test-id="$testId('featuresEdit')"
  >
    <InputLabel text="Available Add-Ons" />
    <div
      v-for="(addOn, index) in optionalAddOns"
      :key="index"
      class="addOn"
      data-test-id="AddOnFeaturesEdit__addOn"
    >
      <Checkbox
        :data-test-id="`addOn__${addOn.productCode}`"
        :isDisabled="isCheckboxDisabled(addOn)"
        label=" "
        :name="addOn.displayName"
        :value="checkboxValue(addOn)"
        @input="handleChange(addOn)"
      >
        <Tooltip
          :hasIndicator="false"
          :isMarkdown="false"
          orientation="top"
          :text="getAttributeDescription(addOn.features[0].name)"
        >
          <span
            class="underlined"
            :data-test-id="$testId(`${addOn.productCode}__label`)"
          >
            {{ addOnLabel(addOn) }}
          </span>
        </Tooltip>
        <TooltipIcon
          v-if="showIcon(addOn)"
          class="tooltipIcon"
          icon="info"
          orientation="right"
          :text="getRequirementsTooltipText(addOn)"
        />
      </Checkbox>
    </div>
    <div class="buttonBar">
      <Button
        :data-test-id="$testId('continue')"
        hasOutline
        text="Continue"
        type="secondary"
        @click="handleContinue"
      />
    </div>
  </div>
</template>

<script>
import {
  Button,
  Checkbox,
  InputLabel,
  Tooltip,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import SubscriptionFeaturesMixin from 'client/Payments/pricingMatrix/SubscriptionFeaturesMixin';
import getAddOnPrice from 'client/Payments/utils/getAddOnPrice';

const {
  getProductCodeByType,
  AddOnProductCodes,
  SubscriptionTypes,
} = SubscriptionConstants;

const {
  boolean,
  description,
  location,
  section,
} = eventProperties;

const pricingModule = 'PricingModule';
const subscriptionCollection = 'SubscriptionCollection';
const checkoutModel = 'CheckoutModel';

export default {
  name: 'AddOnFeaturesEdit',

  components: {
    Button,
    Checkbox,
    InputLabel,
    Tooltip,
    TooltipIcon,
  },

  mixins: [SubscriptionFeaturesMixin],

  props: {
    location: {
      type: String,
      required: true,
    },
    section: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapGetters(subscriptionCollection, [
      'getOptionalAddOns',
      // this is the subscription that is returned by the API
      'getSubscription',
    ]),

    ...mapGetters(checkoutModel, {
      selectedAddOns: 'getSelectedAddOns',
      // this is what is currently have selected in the cart
      subscription: 'item',
    }),

    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
    }),

    isDeviceMgmtSelected() {
      return !!this.selectedAddOns
        .find((currAddOn) => currAddOn.productCode === AddOnProductCodes.deviceMgmt);
    },

    optionalAddOns() {
      return this.getOptionalAddOns(this.subscription.productCode);
    },

    selectedAddOnProductCodes() {
      return this.selectedAddOns
        .map((currAddOn) => currAddOn.productCode);
    },

    subscriptionProductCode() {
      return this.getSubscription(this.subscription.productCode).productCode;
    },
  },

  methods: {
    ...mapActions(checkoutModel, [
      'removeAllAddOnsExceptPremium',
      'toggleAddOn',
      'updateItem',
    ]),

    addOnLabel(addOn) {
      const price = getAddOnPrice(addOn, this.isAnnual, this.subscription.productCode);

      return `${addOn.displayName} ($${price}/user/mo)`;
    },

    checkboxValue(addOn) {
      let value;
      if ((addOn.productCode !== AddOnProductCodes.premiumSupport
        && addOn.productCode !== AddOnProductCodes.passwordManagement)
        && (this.subscriptionProductCode
          === getProductCodeByType(SubscriptionTypes.sso)
        || this.subscriptionProductCode
          === getProductCodeByType(SubscriptionTypes.coreDirectory))) {
        value = this.selectedAddOnProductCodes.includes(addOn.productCode)
          && this.isDeviceMgmtSelected;
      } else {
        value = this.selectedAddOnProductCodes.includes(addOn.productCode);
      }
      return value;
    },

    getRequirementsTooltipText(addOn) {
      return `${addOn.displayName} requires Device Management / MDM to be selected`;
    },

    handleChange(addOn) {
      this.toggleAddOn(addOn);
      const checkboxState = this.selectedAddOnProductCodes.includes(addOn.productCode);

      EventHelper.triggerCheckboxClicked({
        [boolean]: checkboxState,
        [description]: `Add-on checkbox set to ${checkboxState} for ${addOn.displayName} add-on on ${this.subscription.displayName} plan with isAnnual set to ${this.isAnnual}`,
        [location]: this.location,
        [section]: this.section,
      });
    },

    handleContinue() {
      this.$emit('continue');
    },

    isCheckboxDisabled(addOn) {
      return (this.subscriptionProductCode === getProductCodeByType(SubscriptionTypes.sso)
      || this.subscriptionProductCode === getProductCodeByType(SubscriptionTypes.coreDirectory))
      && addOn.requires
      && !this.isDeviceMgmtSelected;
    },

    showIcon(addOn) {
      return (this.subscriptionProductCode === getProductCodeByType(SubscriptionTypes.sso)
      || this.subscriptionProductCode === getProductCodeByType(SubscriptionTypes.coreDirectory))
      && addOn.requires;
    },
  },
};
</script>

<style scoped>
.addOn {
  margin-bottom: var(--jc-spacer-small);
}

.addOnFeaturesEdit {
  display: flex;
  flex-direction: column;
}

.buttonBar {
  display: flex;
  justify-content: flex-end;
}

.underlined {
  text-decoration: underline;
}

.tooltipIcon.tooltipIcon {
  bottom: var(--jc-spacer-negative-small);
  position: absolute;
}
</style>
